import React, { Suspense, startTransition, useEffect } from 'react';

import isEmpty from 'lodash/isEmpty';
import { graphql, type PreloadedQuery, usePreloadedQuery, useQueryLoader } from 'react-relay';

import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import type { GlobalShortcutsItem, ShortcutsConfig } from '../types';

import globalShortcutsRelayClientQueryNode, {
	type GlobalShortcutsRelayClientQuery,
} from './__generated__/GlobalShortcutsRelayClientQuery.graphql';
import type {
	InferShortcutsOutput,
	UseGlobalShortcutDataInputBase,
} from './use-global-shortcuts-data/types';
import { useGlobalShortcutsDataRelay } from './use-global-shortcuts-data/useGlobalShortcutsDataRelay';

export type GlobalShortcutsRelayClientProps<
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
> = UseGlobalShortcutDataInputBase & {
	children: (shortcutItems: InferShortcutsOutput<T>) => React.ReactNode;
	shortcutsConfig: T;
};

/**
 * Standalone GAS Relay entry: includes `RelayEnvironmentProvider` and `Suspense` so callers do not
 * need to wrap the tree. Suspense fallback uses `GlobalShortcutsRelayClientSkip` (no preload, same merge path).
 */
export default function GlobalShortcutsRelayClient<
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
>(props: GlobalShortcutsRelayClientProps<T>): React.JSX.Element {
	return (
		<RelayEnvironmentProvider>
			<Suspense fallback={<GlobalShortcutsRelayClientSkip {...props} />}>
				<GlobalShortcutsRelayClientImpl {...props} />
			</Suspense>
		</RelayEnvironmentProvider>
	);
}

function GlobalShortcutsRelayClientImpl<
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
>(props: GlobalShortcutsRelayClientProps<T>): React.JSX.Element {
	const shouldFetch =
		Boolean(props.cloudId) && !props.isShortcutsStatic && !isEmpty(props.shortcutsConfig);

	const [queryRef, loadQuery] = useQueryLoader<GlobalShortcutsRelayClientQuery>(
		globalShortcutsRelayClientQueryNode,
	);

	useEffect(() => {
		if (!shouldFetch) {
			return;
		}
		startTransition(() => {
			loadQuery({ cloudId: props.cloudId ?? '' });
		});
	}, [shouldFetch, props.cloudId, loadQuery]);

	if (!shouldFetch || queryRef == null) {
		return <GlobalShortcutsRelayClientSkip {...props} />;
	}

	return <GlobalShortcutsRelayClientPreloaded queryRef={queryRef} {...props} />;
}

function GlobalShortcutsRelayClientSkip<
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
>({
	children,
	shortcutsConfig,
	cloudId,
	onError,
	product,
	onUpdateGetFilteredProductLinks,
}: GlobalShortcutsRelayClientProps<T>): React.JSX.Element {
	const shortcutItems = useGlobalShortcutsDataRelay({
		queryRef: undefined,
		shortcutsConfig,
		cloudId,
		onError,
		product,
		onUpdateGetFilteredProductLinks,
	});

	return <>{children(shortcutItems as InferShortcutsOutput<T>)}</>;
}

function GlobalShortcutsRelayClientPreloaded<
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
>({
	queryRef,
	children,
	shortcutsConfig,
	cloudId,
	onError,
	product,
	onUpdateGetFilteredProductLinks,
}: GlobalShortcutsRelayClientProps<T> & {
	queryRef: PreloadedQuery<GlobalShortcutsRelayClientQuery>;
}): React.JSX.Element {
	const queryData = usePreloadedQuery<GlobalShortcutsRelayClientQuery>(
		graphql`
			query GlobalShortcutsRelayClientQuery($cloudId: ID!) {
				...useGlobalShortcutsDataRelay
			}
		`,
		queryRef,
	);

	const shortcutItems = useGlobalShortcutsDataRelay({
		queryRef: queryData,
		shortcutsConfig,
		cloudId,
		onError,
		product,
		onUpdateGetFilteredProductLinks,
	});

	return <>{children(shortcutItems as InferShortcutsOutput<T>)}</>;
}
